/**
 * Company Announcements feed.
 *
 * Every role sees the same feed (server filters by targeting). HR admins
 * additionally get a "Drafts & scheduled" toggle, a "New announcement"
 * button, and per-row edit/delete/read-receipts actions.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Megaphone,
  Pin,
  Plus,
  Paperclip,
  MoreHorizontal,
  Eye,
  Pencil,
  Trash2,
  Clock,
} from "lucide-react";
import { pageTransition } from "@/lib/utils";
import { PageHeader } from "@/components/layout/PageHeader";
import { Card } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { EmptyState } from "@/components/shared/EmptyState";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/useToast";
import {
  deleteAnnouncement,
  listAnnouncements,
  markAnnouncementRead,
  type Announcement,
  type AnnouncementCategory,
  type AnnouncementPriority,
} from "@/api/announcements";
import { AnnouncementFormModal } from "@/components/announcements/AnnouncementFormModal";
import { ReadReceiptsModal } from "@/components/announcements/ReadReceiptsModal";

const CATEGORY_LABEL: Record<AnnouncementCategory, string> = {
  general: "General",
  policy: "Policy",
  event: "Event",
  welcome: "Welcome",
  urgent: "Urgent",
};
const CATEGORY_BADGE: Record<
  AnnouncementCategory,
  "default" | "success" | "warning" | "danger" | "info"
> = {
  general: "default",
  policy: "info",
  event: "success",
  welcome: "success",
  urgent: "danger",
};
const PRIORITY_BADGE: Record<
  AnnouncementPriority,
  "default" | "success" | "warning" | "danger" | "info"
> = {
  normal: "default",
  high: "warning",
  urgent: "danger",
};

function relativeTime(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const mins = Math.round(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString();
}

function BodyPreview({ text }: { text: string }) {
  const [expanded, setExpanded] = useState(false);
  const long = text.length > 320;
  const display = !expanded && long ? `${text.slice(0, 320)}…` : text;
  return (
    <div>
      <p className="whitespace-pre-wrap text-sm text-text-primary leading-relaxed">
        {display}
      </p>
      {long && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-2 text-xs font-medium text-accent hover:underline"
        >
          {expanded ? "Show less" : "Read more"}
        </button>
      )}
    </div>
  );
}

export function AnnouncementsPage() {
  const { user } = useAuth();
  const { addToast } = useToast();
  const isAdmin = user?.role === "SUPER_ADMIN" || user?.role === "HR_ADMIN";

  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [includeDrafts, setIncludeDrafts] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Announcement | null>(null);
  const [receiptsFor, setReceiptsFor] = useState<string | null>(null);
  const [menuFor, setMenuFor] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await listAnnouncements(includeDrafts && isAdmin);
      setItems(rows);
    } catch {
      addToast("error", "Failed to load announcements");
    } finally {
      setLoading(false);
    }
  }, [includeDrafts, isAdmin, addToast]);

  useEffect(() => {
    load();
  }, [load]);

  // Honor ?open=<id> — deep-link from notifications / reminder emails.
  const openId = searchParams.get("open");
  useEffect(() => {
    if (!openId || !items.length) return;
    const target = items.find((a) => a.id === openId);
    if (target && !target.is_read && target.published_at) {
      markAnnouncementRead(openId).catch(() => {});
    }
    // Strip the query param once we've reacted so back-nav doesn't re-fire.
    const next = new URLSearchParams(searchParams);
    next.delete("open");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openId, items.length]);

  const onMarkRead = async (a: Announcement) => {
    if (a.is_read || !a.published_at) return;
    try {
      await markAnnouncementRead(a.id);
      setItems((prev) =>
        prev.map((x) => (x.id === a.id ? { ...x, is_read: true } : x))
      );
    } catch {
      /* ignore */
    }
  };

  const onDelete = async (id: string) => {
    if (!window.confirm("Delete this announcement? This cannot be undone.")) return;
    try {
      await deleteAnnouncement(id);
      setItems((prev) => prev.filter((a) => a.id !== id));
      addToast("success", "Announcement deleted");
    } catch {
      addToast("error", "Failed to delete");
    }
  };

  const subtitle = useMemo(() => {
    if (loading) return "Loading…";
    const unread = items.filter((a) => !a.is_read && a.status === "published").length;
    if (unread === 0) return `${items.length} ${items.length === 1 ? "post" : "posts"}`;
    return `${unread} unread · ${items.length} total`;
  }, [items, loading]);

  return (
    <motion.div {...pageTransition}>
      <PageHeader
        title="Announcements"
        subtitle={subtitle}
        actions={
          isAdmin ? (
            <>
              <label className="flex items-center gap-2 text-sm text-text-primary">
                <input
                  type="checkbox"
                  checked={includeDrafts}
                  onChange={(e) => setIncludeDrafts(e.target.checked)}
                  className="rounded border-border"
                />
                Show drafts & scheduled
              </label>
              <Button
                onClick={() => {
                  setEditing(null);
                  setFormOpen(true);
                }}
              >
                <Plus className="h-4 w-4" />
                New announcement
              </Button>
            </>
          ) : null
        }
      />

      {loading ? (
        <Card>
          <p className="text-sm text-text-muted text-center py-8">Loading announcements…</p>
        </Card>
      ) : items.length === 0 ? (
        <Card>
          <EmptyState
            icon={Megaphone}
            title="No announcements yet"
            description={
              isAdmin
                ? "Post the first one so everyone stays in the loop."
                : "Your HR team hasn't posted anything yet."
            }
            action={
              isAdmin
                ? {
                    label: "New announcement",
                    onClick: () => {
                      setEditing(null);
                      setFormOpen(true);
                    },
                  }
                : undefined
            }
          />
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((a) => {
            const isUrgent = a.priority === "urgent" || a.category === "urgent";
            const isUnread = !a.is_read && a.status === "published";
            return (
              <Card
                key={a.id}
                className={`relative ${
                  isUrgent ? "border-l-4 border-l-red-500" : ""
                } ${isUnread ? "ring-1 ring-accent/30" : ""}`}
              >
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      {a.is_pinned && (
                        <Pin className="h-4 w-4 text-accent" aria-label="Pinned" />
                      )}
                      <h3
                        className="text-base font-semibold font-heading text-text-primary cursor-pointer"
                        onClick={() => onMarkRead(a)}
                      >
                        {a.title}
                      </h3>
                      {isUnread && (
                        <span
                          className="h-2 w-2 rounded-full bg-accent"
                          aria-label="Unread"
                        />
                      )}
                      <Badge variant={CATEGORY_BADGE[a.category]}>
                        {CATEGORY_LABEL[a.category]}
                      </Badge>
                      {a.priority !== "normal" && (
                        <Badge variant={PRIORITY_BADGE[a.priority]}>
                          {a.priority}
                        </Badge>
                      )}
                      {a.status !== "published" && (
                        <Badge variant="default">{a.status}</Badge>
                      )}
                    </div>
                    <div className="text-xs text-text-muted mb-3">
                      {a.author?.name ?? "Unknown"} ·{" "}
                      {a.status === "scheduled"
                        ? `Scheduled for ${new Date(a.scheduled_for!).toLocaleString()}`
                        : a.status === "draft"
                          ? "Draft"
                          : relativeTime(a.published_at)}
                      {a.expires_at && (
                        <>
                          {" · "}
                          <Clock className="inline h-3 w-3" /> expires{" "}
                          {new Date(a.expires_at).toLocaleDateString()}
                        </>
                      )}
                    </div>
                    <BodyPreview text={a.body} />
                    {a.attachment_url && (
                      <a
                        href={a.attachment_url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-accent hover:underline mt-3"
                      >
                        <Paperclip className="h-3 w-3" />
                        {a.attachment_name || "Attachment"}
                      </a>
                    )}
                  </div>

                  {isAdmin && (
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() =>
                          setMenuFor((prev) => (prev === a.id ? null : a.id))
                        }
                        className="rounded-lg p-1.5 text-text-muted hover:bg-surface-muted"
                        aria-label="Actions"
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </button>
                      {menuFor === a.id && (
                        <div
                          className="absolute right-0 top-8 z-10 w-44 rounded-lg border border-border bg-surface shadow-lg py-1"
                          onMouseLeave={() => setMenuFor(null)}
                        >
                          <button
                            type="button"
                            onClick={() => {
                              setMenuFor(null);
                              setReceiptsFor(a.id);
                            }}
                            className="flex w-full items-center gap-2 px-3 py-1.5 text-sm text-text-primary hover:bg-surface-muted"
                          >
                            <Eye className="h-3.5 w-3.5" />
                            Read receipts
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setMenuFor(null);
                              setEditing(a);
                              setFormOpen(true);
                            }}
                            className="flex w-full items-center gap-2 px-3 py-1.5 text-sm text-text-primary hover:bg-surface-muted"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setMenuFor(null);
                              onDelete(a.id);
                            }}
                            className="flex w-full items-center gap-2 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <AnnouncementFormModal
        isOpen={formOpen}
        onClose={() => setFormOpen(false)}
        onSaved={load}
        existing={editing}
      />
      <ReadReceiptsModal
        isOpen={receiptsFor !== null}
        announcementId={receiptsFor}
        onClose={() => setReceiptsFor(null)}
      />
    </motion.div>
  );
}
